/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import { AppBar, Checkbox, Paper, Tab, Tabs } from "@material-ui/core";
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Collapse,
  Row,
  Input
} from "reactstrap";
import Toastr from "toastr";
import { useQueryClient } from "react-query";
import { TabPanel } from "../components/TabPanel";
import UploadDropzone from "../components/UploadDropzone";
import { useSaveExecutionMutation } from "../hooks/useSaveExecutionMutation";
import { useAskQuestion } from "../hooks/useAskQuestion";
import { ExecutionInputType } from "../types";
import { toTitleCase } from "../utils/toTitleCase";
import ExtractedText from "../components/ExtractedText";
import Typewriter from 'typewriter-effect';
import { copyTextToClipboard } from "../utils/copyToClipboard";
import { useGetQuestions } from "../hooks/useGetQuestions";
import SingleQuestion from "../components/SingleQuestion";
import { useAnalyseExecutionMutation } from "../hooks/useAnalyseExecutionMutation";
import WordCloudSection from "../components/WordCloudSection";
import KeyphraseSection from "../components/KeyphraseSection";
// import { useParaphraseQuestion } from "../hooks/useParaphraseQuestion";
import {
  ExpandMore, ExpandLess, NavigateNext, NavigateBefore,
  MenuBook, DynamicFeed, Equalizer, HourglassEmpty,
  AllInclusive, ArrowBack, ArrowForward
} from "@material-ui/icons";
import { useCurrentUser } from '../hooks/useCurrentUser';
import { UserPermissions } from '../types';

const SearchPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(ExecutionInputType.text);
  const [inputText, setInputText] = useState("");
  const [inputFiles, setInputFiles] = useState<File[]>([]);
  const [finalQuestion, setFinalQuestion] = useState("");
  const [questionText, setQuestionText] = useState("");
  const [activeSource, setActiveSource] = useState(0);
  const [activeExtractedTextIdx, setActiveExtractedTextIdx] = useState(0);
  const [isConfidential, setIsConfidential] = useState(false);
  const [isTable, setIsTable] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [finalSearchTerm, setFinalSearchTerm] = useState("");
  const [view, setView] = useState<"text" | "redacted">("text");
  const [txtCollapsed, setTxtCollapsed] = useState(false);
  const [gptQna, setGptQna] = useState(false)
  const [analysisCollapsed] = useState(false);
  const [currentDocumentPage, setCurrentDocumentPage] = useState(0);
  const [showRedactedReference, setShowRedactedReference] = useState(false)
  const [callFromOpenLLM, setCallFromOpenLLM] = useState<boolean>(false)
  const [refrenceSearch, setRefrenceSearch] = useState<string>("");
  const [isFilter, setIsFilter] = useState<boolean>(false);
  const [originalSources, setOriginalSources] = useState<Array<any>>([]);
  
  const submitSearchReferences = (e: any) =>{
    e.preventDefault();
    searchReferences();
  }

  const searchReferences = () => {
    if(!isFilter && askQuestionResponse && askQuestionResponse.questions && askQuestionResponse.questions[0].qnaResponse) {
      setOriginalSources(askQuestionResponse.questions[0].qnaResponse.sources[activeSource]);
    }
    setIsFilter(false);
    if (refrenceSearch && askQuestionResponse && askQuestionResponse.questions && askQuestionResponse.questions[0].qnaResponse) {
      const filteredSources = originalSources.filter((ele) => {
        return ele.value.includes(refrenceSearch);
      });
      
      askQuestionResponse.questions[0].qnaResponse.sources[activeSource] = filteredSources;
      setIsFilter(true);
    } else if(askQuestionResponse && askQuestionResponse.questions && askQuestionResponse.questions[0].qnaResponse && originalSources.length) {
      askQuestionResponse.questions[0].qnaResponse.sources[activeSource] = originalSources;
      setIsFilter(false);
    }
  };

  useEffect(()=>{
    searchReferences()
  },[isFilter, refrenceSearch])

  const { data: currentUserData } = useCurrentUser();

  const tabs = useMemo(() => {
    return Object.values(ExecutionInputType);
  }, []);

  const nextDocumentPage = () => {
    let nextPage = currentDocumentPage + 1;

    setCurrentDocumentPage(nextPage);
  }

  const previousDocumentPage = () => {
    if (currentDocumentPage > 0) {
      let nextPage = currentDocumentPage - 1;

      setCurrentDocumentPage(nextPage);
    }
  }

  const {
    mutate: saveExecution,
    data: saveExecutionResponse,
    isLoading: saveExecutionLoading,
  } = useSaveExecutionMutation((err, msg) => {
    if (err) {
      Toastr.error(msg || "Something went wrong. Please try again later");
    }
  });

  // const {
  //   data: paraphrasedQuestions,
  //   refetch: paraphraseQuestion,
  // } = useParaphraseQuestion(finalQuestion, saveExecutionResponse?.id || 0)

  const {
    mutate: analyseExecution,
    data: analyseExecutionResponse,
    isLoading: analyseExecutionLoading,
    reset: resetAnalyseExecution,
    isError: analyseExecutionError,
  } = useAnalyseExecutionMutation((err, msg) => {
    if (err) {
      Toastr.error(msg || "Something went wrong. Please try again later");
    }
  });

  const {
    refetch: askQuestion,
    data: askQuestionResponse,
    isFetching: askQuestionLoading,
    isError: askQuestionError,
  } = useAskQuestion(finalQuestion, saveExecutionResponse?.id || 0, gptQna, callFromOpenLLM);

  const { data: questions, refetch: getQuestions } = useGetQuestions(saveExecutionResponse?.id || 0, {
    enabled: typeof saveExecutionResponse?.id === "number",
  })

  const pastQuestions = useMemo(() => {
    if (!questions) return []
    return questions.slice(1)
  }, [questions])

  const handleSubmit = () => {
    let parsedTexts: string[] = [];
    if (
      activeTab === ExecutionInputType.text ||
      activeTab === ExecutionInputType.url
    ) {
      if (inputText.trim().length === 0) {
        alert("Please enter some text/urls");
        return;
      }

      if (activeTab === ExecutionInputType.url) {
        parsedTexts = inputText
          .split("\n")
          .filter((text) => text.trim().length > 0)
          .map((text) => text.trim());
        const invalidUrls = parsedTexts.filter((url) => {
          try {
            const u = new URL(url);
            if (u.protocol !== "http:" && u.protocol !== "https:") {
              return true;
            }
            return false;
          } catch (e) {
            return true;
          }
        });
        if (invalidUrls.length > 0) {
          alert(`Invalid URLs: ${invalidUrls.join(", ")}`);
          return;
        }
        parsedTexts = Array.from(new Set(parsedTexts));
      } else {
        parsedTexts = [inputText.trim()];
      }
    } else if (activeTab === ExecutionInputType.file) {
      if (inputFiles.length === 0) {
        alert("Please select some files");
        return;
      }
      if (inputFiles.length > 25) {
        alert('Please select at most 25 files');
        return;
      }
      const totalFileSizeBytes = inputFiles.reduce(
        (acc, file) => acc + file.size,
        0
      );

      if (totalFileSizeBytes > 60 * 1024 * 1024) {
        alert('Total file size should be at most 60MB');
        return;
      }
    }

    queryClient.removeQueries(["ask"]);
    setView("text")
    setFinalQuestion("");
    setQuestionText("");
    setSearchTerm("");
    setFinalSearchTerm("");
    setActiveExtractedTextIdx(0)
    setCurrentDocumentPage(0)
    setActiveSource(0)
    resetAnalyseExecution()
    document.querySelector("#qna_section")?.scrollIntoView({
      behavior: "smooth",
    });
    saveExecution({
      inputType: activeTab,
      inputText: parsedTexts.join(","),
      files: inputFiles,
      isConfidential: activeTab === ExecutionInputType.url ? false : isConfidential,
      isTable: activeTab === ExecutionInputType.url ? false : isTable,
    });
  };

  const handleAskQuestion = (gq: boolean, openllm: boolean) => {
    if (questionText.trim().length === 0) {
      alert("Please enter a question");
      return;
    }
    if (questionText.trim().length > 4000) {
      alert('Question should be at most 4000 characters');
      return;
    }
    if (!saveExecutionResponse) {
      return;
    }
    if (!saveExecutionResponse.numWords || !saveExecutionResponse.numSentences) {
      alert('No text to ask question from!')
      return;
    }
    setSearchTerm("");
    setFinalSearchTerm("");
    setActiveSource(0)
    setTxtCollapsed(false);
    setCallFromOpenLLM(openllm);
    setFinalQuestion(questionText);
    setGptQna(gq)
    queryClient.removeQueries(["paraphrase"]);
  };

  useEffect(() => {
    if (analyseExecutionResponse || analyseExecutionError) return

    if (saveExecutionResponse?.id && !analyseExecutionLoading) {
      analyseExecution({
        executionId: saveExecutionResponse.id,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyseExecutionError, saveExecutionResponse, analyseExecutionResponse, analyseExecutionLoading])

  useEffect(() => {
    if (!askQuestionResponse) return
    queryClient.removeQueries(["execution", askQuestionResponse?.id || 0, "questions"]);
    getQuestions();
    // paraphraseQuestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askQuestionResponse])

  useEffect(() => {
    if (askQuestionError) {
      Toastr.error("Something went wrong. Please try again later");
    }
  }, [askQuestionError])

  const handleCopyAllSources = () => {
    if (askQuestionResponse?.questions === undefined ||
      !askQuestionResponse.questions[0] || !askQuestionResponse.questions[0].qnaResponse) return

    const sources = askQuestionResponse.questions[0].qnaResponse.sources[activeSource]
    copyTextToClipboard(sources.map((source) => source.value).join("\n"))
    Toastr.success("Copied to clipboard")
  }

  const handleCopyAllOutput = () => {
    if (askQuestionResponse?.questions === undefined ||
      !askQuestionResponse.questions[0] || !askQuestionResponse.questions[0].qnaResponse) return

    const responses = askQuestionResponse.questions[0].qnaResponse.responses
    copyTextToClipboard(responses.join("\n"))
    Toastr.success("Copied to clipboard")
  }

  useEffect(() => {
    if (saveExecutionResponse && saveExecutionResponse.status !== "parsed") {
      Toastr.error("Could not parse the input. Please try again later");
    }
  }, [saveExecutionResponse]);

  useEffect(() => {
    if (finalQuestion.trim().length > 0) {
      askQuestion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalQuestion, gptQna, callFromOpenLLM]);

  const sources = useMemo(() => {
    if (askQuestionResponse?.questions === undefined || askQuestionResponse?.questions.length === 0) return []
    if (askQuestionResponse?.questions[0].qnaResponse?.sources === undefined) return []
    return askQuestionResponse.questions[0].qnaResponse.sources
  }, [askQuestionResponse])


  const wordsOutput = useMemo(() => {
    if (askQuestionResponse?.questions === undefined || askQuestionResponse?.questions.length === 0) return 0
    try {
      return (Array.isArray(askQuestionResponse.questions[0]!.qnaResponse?.responses) ? askQuestionResponse.questions[0]!.qnaResponse!.responses : []).reduce((acc, curr) => acc + curr).split(' ').filter(w => w.length > 0).length
    } catch (error) {
      return 0
    }
  }, [askQuestionResponse])

  const wordsSources = useMemo(() => {
    return (sources.length === 0 ? [[{ value: '' }]] : sources)?.flat()?.map(c => c.value)?.reduce((acc, curr) => acc + curr)?.split(' ')?.filter(w => w.length > 0)?.length || 0
  }, [sources])

  return (
    <>
      <div
        className="header pb-6 pt-5 pt-md-8"
        style={{
          height: '500px',
          backgroundColor: '#2D315C',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundImage: 'url(/background-header-infinity.jpg)',
        }}
      >
        {/* Mask */}
        <span className="mask bg-gradient-default opacity-8" />
        <Container style={{ display: 'flex', flexDirection: 'column' }} fluid>
          <div style={{ alignSelf: 'center' }}>
            <AppBar
              position="relative"
              style={{ background: '#fff', color: '#111', opacity: '.8' }}
              className="qp_tabs"
            >
              <Tabs
                value={tabs.indexOf(activeTab)}
                onChange={(_, idx) =>
                  saveExecutionLoading || askQuestionLoading
                    ? null
                    : setActiveTab(tabs[idx])
                }
              >
                {tabs.map((label) => {
                  return <Tab key={label} label={toTitleCase(label)} />;
                })}
              </Tabs>
            </AppBar>
          </div>
          <Row>
            <Col>
              <div style={{ textAlign: 'center' }}>
                <TabPanel
                  style={{ display: 'inline-block', width: '75%' }}
                  index={0}
                  value={tabs.indexOf(activeTab)}
                >
                  <textarea
                    className="form-control"
                    rows={5}
                    placeholder="Enter the text here"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    style={{ letterSpacing: '.05em' }}
                  />
                </TabPanel>
                <TabPanel
                  style={{ display: 'inline-block', width: '75%' }}
                  index={1}
                  value={tabs.indexOf(activeTab)}
                >
                  <UploadDropzone
                    setFiles={setInputFiles}
                    isFileUploaded={!!saveExecutionResponse}
                  />
                </TabPanel>
                <TabPanel
                  style={{ display: 'inline-block', width: '75%' }}
                  index={2}
                  value={tabs.indexOf(activeTab)}
                >
                  <textarea
                    className="form-control"
                    rows={5}
                    placeholder="Enter the URLs here"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    style={{ letterSpacing: '.05em' }}
                  />
                </TabPanel>
              </div>
              <div className="text-center">
                {activeTab !== ExecutionInputType.url && (
                  <div style={{ display: 'inline' }}>
                    <Checkbox
                      checked={isConfidential}
                      onChange={(e) => setIsConfidential(e.target.checked)}
                      inputProps={{ 'aria-label': 'primary checkbox' }}
                      style={{ color: '#fff' }}
                    />
                    <label style={{ color: 'white' }}>
                      Is this data confidential?
                    </label>
                  </div>
                )}
                {activeTab === ExecutionInputType.file && (
                  <div style={{ display: 'inline' }}>
                    <Checkbox
                      checked={isTable}
                      onChange={(e) => setIsTable(e.target.checked)}
                      inputProps={{ 'aria-label': 'primary checkbox' }}
                      style={{ color: '#fff' }}
                    />
                    <label style={{ color: 'white' }}>
                      Quantitative processing?
                    </label>
                  </div>
                )}
              </div>
              <div className="text-center">
                {saveExecutionLoading || askQuestionLoading ? (
                  <svg
                    height="70px"
                    viewBox="0 0 187.3 93.7"
                    preserveAspectRatio="xMidYMid meet"
                  >
                    <path
                      stroke="rgb(215, 61, 50)"
                      id="outline"
                      fill="none"
                      stroke-width="4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-miterlimit="10"
                      d="M93.9,46.4c9.3,9.5,13.8,17.9,23.5,17.9s17.5-7.8,17.5-17.5s-7.8-17.6-17.5-17.5c-9.7,0.1-13.3,7.2-22.1,17.1 				c-8.9,8.8-15.7,17.9-25.4,17.9s-17.5-7.8-17.5-17.5s7.8-17.5,17.5-17.5S86.2,38.6,93.9,46.4z"
                    />
                    <path
                      id="outline-bg"
                      opacity="0.05"
                      fill="none"
                      stroke="#fff"
                      stroke-width="4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-miterlimit="10"
                      d="				M93.9,46.4c9.3,9.5,13.8,17.9,23.5,17.9s17.5-7.8,17.5-17.5s-7.8-17.6-17.5-17.5c-9.7,0.1-13.3,7.2-22.1,17.1 				c-8.9,8.8-15.7,17.9-25.4,17.9s-17.5-7.8-17.5-17.5s7.8-17.5,17.5-17.5S86.2,38.6,93.9,46.4z"
                    />
                  </svg>
                ) : (
                  <Button
                    color="primary"
                    onClick={handleSubmit}
                    disabled={saveExecutionLoading || askQuestionLoading}
                    style={{
                      width: '130px',
                      fontSize: '20px',
                      background: '#FF5252',
                      border: '#FF5252',
                    }}
                    className="btn-sm "
                  >
                    <AllInclusive />
                  </Button>
                )}
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              {saveExecutionResponse &&
                saveExecutionResponse.status === 'parsed' &&
                saveExecutionResponse.inputs &&
                saveExecutionResponse.inputs.length > 0 && (
                  <Container
                    style={{
                      color: '#fff',
                      lineHeight: '1.8em',
                      letterSpacing: '.08em',
                      fontWeight: '600',
                      fontSize: '14px',
                    }}
                    className="text-center mt-1"
                    fluid
                  >
                    Extracted:{' '}
                    <span>
                      {saveExecutionResponse.numWords || 0}&nbsp;words,&nbsp;
                      {saveExecutionResponse.numSentences || 0}&nbsp;sentences
                    </span>
                  </Container>
                )}
            </Col>
          </Row>
        </Container>
      </div>

      {(!saveExecutionResponse ||
        saveExecutionResponse.status !== 'parsed') && (
        <Container fluid>
          <Row className="mt-5 text-center">
            <Col>
              <span>
                <HourglassEmpty /> Please upload your content.{' '}
              </span>
            </Col>
          </Row>
        </Container>
      )}

      {saveExecutionResponse && saveExecutionResponse.status === 'parsed' && (
        <Container fluid>
          <Row id="qna_section" className="mt-5">
            <Col>
              <h1>
                <DynamicFeed /> QNA
              </h1>
            </Col>
          </Row>
        </Container>
      )}

      {saveExecutionResponse && saveExecutionResponse.status === 'parsed' && (
        <Container fluid>
          <Row>
            <Col>
              <Card style={{ background: 'transparent', border: 'none' }}>
                <CardBody>
                  <Row>
                    <Col>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter the question here"
                        value={questionText}
                        onChange={(e) => setQuestionText(e.target.value)}
                        style={{ border: '1px solid rgb(45, 49, 92, .5)' }}
                      />
                    </Col>
                  </Row>
                  {!askQuestionLoading && (
                    <Row className="mt-2">
                      <Col>
                        <Button
                          size="sm"
                          className="btn-sm btn-warning btn btn-secondary"
                          color="primary"
                          style={{ maxWidth: 'fit-content' }}
                          onClick={() => {
                            handleAskQuestion(false, false);
                          }}
                          disabled={askQuestionLoading}
                        >
                          {askQuestionLoading ? 'Loading...' : 'Ask Question'}
                        </Button>
                        <Button
                          size="sm"
                          className="btn-sm btn-warning btn btn-secondary"
                          color="primary"
                          style={{ maxWidth: 'fit-content' }}
                          onClick={() => {
                            handleAskQuestion(true, false);
                          }}
                          disabled={askQuestionLoading}
                        >
                          {askQuestionLoading ? 'Loading...' : 'Ask ChatGPT'}
                        </Button>
                        {currentUserData &&
                          currentUserData.roles.includes(
                            UserPermissions.internalLLM
                          ) && (
                            <Button
                              size="sm"
                              className="btn-sm btn-warning btn btn-secondary"
                              color="primary"
                              style={{ maxWidth: 'fit-content' }}
                              onClick={() => {
                                handleAskQuestion(false, true);
                              }}
                              disabled={askQuestionLoading}
                            >
                              {askQuestionLoading
                                ? 'Loading...'
                                : 'Ask Internal LLM'}
                            </Button>
                          )}
                      </Col>
                    </Row>
                  )}
                  {askQuestionLoading && (
                    <Row className="mt-2">
                      <Col className="text-center">
                        <Typewriter
                          options={{
                            loop: true,
                          }}
                          onInit={(typewriter) => {
                            typewriter
                              .typeString('Please wait...')
                              .pauseFor(1000)
                              .deleteAll()
                              .typeString('Thinking intensifies...')
                              .pauseFor(1000)
                              .deleteAll()
                              .typeString('Still thinking...')
                              .pauseFor(1000)
                              .start();
                          }}
                        />
                      </Col>
                    </Row>
                  )}
                  {/* {
                          paraphrasedQuestions &&
                          paraphrasedQuestions.length > 0 &&
                          <Col className="mt-2">
                            <Row style={{
                              // justifyContent: "center",
                            }}>
                              {paraphrasedQuestions.map((question, idx) => (
                                <Button
                                  key={idx}
                                  className="mr-2 mb-2 paraphrase-ques-btn"
                                  color="transparent"
                                  style={{
                                    color: '#9fa0a3',
                                    borderRadius: '5px',
                                    padding: '3px 8px',
                                    background: "#fff"
                                  }}
                                  onClick={() => {
                                    setQuestionText(question);
                                    setSearchTerm("");
                                    setFinalSearchTerm("");
                                    setTxtCollapsed(true);
                                    setMappingCollapsed(true);
                                    setFinalQuestion(question);
                                    setActiveSource(0)
                                    queryClient.removeQueries(["paraphrase"]);
                                  }}
                                >
                                  <span>
                                    {question}
                                  </span>
                                </Button>
                              ))}
                            </Row>
                          </Col>
                        } */}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      )}

      {saveExecutionResponse && saveExecutionResponse.status === 'parsed' && (
        <div>
          {!askQuestionLoading &&
            askQuestionResponse &&
            askQuestionResponse.questions &&
            askQuestionResponse.questions.length > 0 &&
            askQuestionResponse.questions[0].qnaResponse && (
              <SingleQuestion
                question={askQuestionResponse.questions[0]}
                key={askQuestionResponse.questions[0].id}
                setActiveExtractedTextIdx={setActiveExtractedTextIdx}
                setCurrentDocumentPage={setCurrentDocumentPage}
                setSearchTerm={setSearchTerm}
                setFinalSearchTerm={setFinalSearchTerm}
                setTxtCollapsed={setTxtCollapsed}
                mapping={askQuestionResponse.questions[0].redactionMapping}
                defaultOpen={true}
                inputType={saveExecutionResponse.inputType}
              />
            )}
        </div>
      )}
      {/* <div className="mt-4" /> */}
      {saveExecutionResponse && saveExecutionResponse.status === 'parsed' && (
        <Container fluid>
          <Row className="mt-5">
            <Col>
              <h1>
                <DynamicFeed /> Previous Questions{' '}
                <small style={{ fontSize: '12px', letterSpacing: '.08em' }}>
                  (Recent First)
                </small>
              </h1>
            </Col>
          </Row>
        </Container>
      )}

      {saveExecutionResponse &&
        saveExecutionResponse.status === 'parsed' &&
        pastQuestions.length < 1 && (
          <Container fluid>Nothing to show here.</Container>
        )}
      {pastQuestions.length > 0 &&
        saveExecutionResponse &&
        pastQuestions.map((q, i) => (
          <SingleQuestion
            question={q}
            key={`${i}. ${q.question}`}
            setActiveExtractedTextIdx={setActiveExtractedTextIdx}
            setCurrentDocumentPage={setCurrentDocumentPage}
            setSearchTerm={setSearchTerm}
            setFinalSearchTerm={setFinalSearchTerm}
            setTxtCollapsed={setTxtCollapsed}
            mapping={q.redactionMapping}
            defaultOpen={false}
            inputType={saveExecutionResponse.inputType}
          />
        ))}

      {saveExecutionResponse && saveExecutionResponse.status === 'parsed' && (
        <Container fluid>
          <Row className="mt-5">
            <Col>
              <h1>
                <MenuBook /> {view === 'text' ? 'Extracted' : 'Redacted'} Text{' '}
              </h1>
            </Col>
          </Row>
        </Container>
      )}

      <Container fluid style={{ display: 'flex', flexDirection: 'column' }}>
        {saveExecutionResponse &&
          saveExecutionResponse.status === 'parsed' &&
          saveExecutionResponse.inputs &&
          saveExecutionResponse.inputs.length > 0 && (
            <Card className="bg-secondary shadow extract-data-card">
              <CardHeader>
                <Row className="align-items-center" id="txt_section">
                  <Col>
                    <div>
                      <small>
                        <b>
                          Total:&nbsp;{saveExecutionResponse.numWords || 0}
                          &nbsp;words,&nbsp;
                          {saveExecutionResponse.numSentences || 0}
                          &nbsp;sentences
                        </b>
                      </small>
                    </div>
                    <div style={{ display: 'inline' }}>
                      <small>
                        File ({activeExtractedTextIdx + 1} of&nbsp;
                        {saveExecutionResponse.inputs.length})
                      </small>
                    </div>
                  </Col>

                  <Col className="text-right">
                    <span className="float-right">
                      {/* TODO: have token appended in react query hook (data can be transformed using `select` option) */}
                      {(saveExecutionResponse.inputType ===
                        ExecutionInputType.file ||
                        saveExecutionResponse.inputType ===
                          ExecutionInputType.url) &&
                        saveExecutionResponse.inputs &&
                        saveExecutionResponse.inputs[
                          activeExtractedTextIdx
                        ] && (
                          <a
                            href={`${
                              saveExecutionResponse.inputs[
                                activeExtractedTextIdx
                              ].inputText
                            }?token=${localStorage.getItem('token')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ marginRight: '10px' }}
                          >
                            <i className="fas fa-link mr-1" />
                          </a>
                        )}
                      {/* {saveExecutionResponse.isConfidential && (
                          <Button
                            size="sm"
                            color="primary"
                            onClick={() => {
                              setView(view === "text" ? "redacted" : "text");
                            }}
                          >
                            {view === "text" ? "Redact" : "Show Text"}
                          </Button>
                        )} */}
                      {saveExecutionResponse.inputs.length > 1 && (
                        <Button
                          size="sm"
                          color="primary"
                          disabled={activeExtractedTextIdx === 0}
                          onClick={() => {
                            setCurrentDocumentPage(0);
                            if (activeExtractedTextIdx > 0) {
                              setActiveExtractedTextIdx(
                                activeExtractedTextIdx - 1
                              );
                            }
                          }}
                        >
                          <NavigateBefore />
                        </Button>
                      )}
                      {saveExecutionResponse.inputs.length > 1 && (
                        <Button
                          size="sm"
                          disabled={
                            activeExtractedTextIdx ===
                            saveExecutionResponse.inputs!.length - 1
                          }
                          color="primary"
                          onClick={() => {
                            if (
                              activeExtractedTextIdx <
                              saveExecutionResponse.inputs!.length - 1
                            ) {
                              setActiveExtractedTextIdx(
                                activeExtractedTextIdx + 1
                              );
                            }

                            setCurrentDocumentPage(0);
                          }}
                        >
                          <NavigateNext />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        color="primary"
                        className="btn-sm"
                        onClick={() => {
                          setTxtCollapsed(!txtCollapsed);
                        }}
                      >
                        {txtCollapsed ? <ExpandMore /> : <ExpandLess />}
                      </Button>
                    </span>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Collapse isOpen={!txtCollapsed}>
                  <Row>
                    <Col>
                      <div className="input-group-alternative input-group">
                        <div
                          className="input-group-prepend"
                          style={{ width: '100%' }}
                        >
                          <span onClick={() => {}} className="input-group-text">
                            <i className="fas fa-search"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search for anything in current page"
                            id="subt_search"
                            value={searchTerm}
                            onChange={(e) => {
                              setSearchTerm(e.target.value);
                            }}
                            onKeyDown={(e) => {
                              if (e.key.toLowerCase() === 'enter') {
                                setFinalSearchTerm(searchTerm);
                              }
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                  {Object.keys(
                    saveExecutionResponse.inputs[activeExtractedTextIdx]
                      ?.extractedText?.text
                  ).length > 1 && (
                    <Row className="mt-2">
                      <Col>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            height: '100%',
                            float: 'right',
                          }}
                        >
                          <span style={{ float: 'left' }}>
                            Page: {currentDocumentPage + 1} /{' '}
                            {
                              Object.keys(
                                saveExecutionResponse.inputs[
                                  activeExtractedTextIdx
                                ]?.extractedText?.text
                              ).length
                            }
                          </span>
                          &nbsp;
                          <Button
                            size="sm"
                            className="btn-primary"
                            disabled={currentDocumentPage < 1}
                            onClick={() => previousDocumentPage()}
                          >
                            <ArrowBack />
                          </Button>
                          {
                            <Button
                              size="sm"
                              className="btn-primary"
                              disabled={
                                Object.keys(
                                  saveExecutionResponse.inputs[
                                    activeExtractedTextIdx
                                  ]?.extractedText?.text
                                ).length <=
                                currentDocumentPage + 1
                              }
                              onClick={() => nextDocumentPage()}
                            >
                              <ArrowForward />
                            </Button>
                          }
                        </div>
                      </Col>
                    </Row>
                  )}
                  <ExtractedText
                    text={
                      saveExecutionResponse.inputs[activeExtractedTextIdx]
                        ?.extractedText?.text[currentDocumentPage]
                    }
                    redactedHTML={
                      saveExecutionResponse.redactedHTML
                        ? saveExecutionResponse.redactedHTML[
                            activeExtractedTextIdx
                          ]
                        : undefined
                    }
                    view={view}
                    searchTerm={finalSearchTerm}
                  />
                </Collapse>
              </CardBody>
            </Card>
          )}
        {/* {saveExecutionResponse &&
          saveExecutionResponse.status === "parsed" &&
          saveExecutionResponse.isConfidential &&
          saveExecutionResponse.mapping &&
          Object.keys(saveExecutionResponse.mapping).length > 0 && (
            <Card className="bg-secondary shadow extract-data-card">
              <CardHeader>
                <Row className="align-items-center">
                  <Col xs="10">
                    <h2 style={{ display: "inline" }}>
                      <VpnLock /> Redaction Mapping
                    </h2>
                  </Col>
                  <Col className="text-right" xs="2">
                    <span className="float-right">
                      <Button
                        size="sm"
                        color="primary"
                        onClick={() => {
                          setMappingCollapsed(!mappingCollapsed);
                        }}
                      >
                        {mappingCollapsed ? <ExpandMore /> : <ExpandLess />}
                      </Button>
                    </span>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody style={{ overflowY: "scroll", maxHeight: "70vh" }}>
                <Collapse isOpen={!mappingCollapsed}>
                  <Table>
                    <thead className="thead-light">
                      <tr>
                        <th scope="col">Original Entity</th>
                        <th scope="col">Redacted Entity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        Object.entries(saveExecutionResponse.mapping).map(
                          ([key, value]) => (
                            <tr key={key}>
                              <td>{key}</td>
                              <td>{value}</td>
                            </tr>
                          )
                        )
                      }
                    </tbody>
                  </Table>
                </Collapse>
              </CardBody>
            </Card>
          )} */}

        {analyseExecutionLoading && (
          <Container fluid className="mb-4 mt-4">
            <Card className="shadow">
              <Paper className="px-4 py-3">
                <div className="mb-3">
                  <h2 style={{ display: 'inline' }}>Loading analysis...</h2>
                </div>
              </Paper>
            </Card>
          </Container>
        )}
      </Container>

      {saveExecutionResponse && saveExecutionResponse.status === 'parsed' && (
        <Container fluid>
          <Row className="mt-5">
            <Col>
              <h1>
                <Equalizer /> Analysis{' '}
              </h1>
            </Col>
          </Row>
        </Container>
      )}

      {analyseExecutionResponse && (
        <Container fluid className="mb-4 mt-1">
          <Card className="shadow">
            <Collapse isOpen={!analysisCollapsed}>
              {(analyseExecutionResponse.wc2Words ||
                analyseExecutionResponse.wc3Words) && (
                <WordCloudSection
                  analyseResponse={analyseExecutionResponse}
                  handleKeyPhrase={(word) => {
                    setSearchTerm(word);
                    setFinalSearchTerm(word);
                    document.querySelector('#txt_section')?.scrollIntoView({
                      behavior: 'smooth',
                    });
                    setTxtCollapsed(false);
                  }}
                />
              )}
              <CardBody
                style={{
                  backgroundColor: '#fff',
                }}
              >
                <hr />
                {analyseExecutionResponse.keyphrases && (
                  <KeyphraseSection
                    analyseResponse={analyseExecutionResponse}
                    handleKeyPhrase={(word) => {
                      setSearchTerm(word);
                      setFinalSearchTerm(word);
                      document.querySelector('#txt_section')?.scrollIntoView({
                        behavior: 'smooth',
                      });
                      setTxtCollapsed(false);
                    }}
                  />
                )}
              </CardBody>
            </Collapse>
          </Card>
        </Container>
      )}
    </>
  );
};

export default SearchPage;
